// Add_User.tsx
import React, { useState, useEffect, useMemo } from "react";
import CustomInput from "@/components/CustomInput/CustomInput";
import {
  useCreateUser,
  useUpdateUser,
  useAllocations,
  useCreditUsage,
} from "@/hooks/useTenantDashboardService";
import { useQueryClient } from "@tanstack/react-query";
import { getTenantById } from "@/api/tenantDashboardService";
import { isValid } from "@/utils/validators";
import type { User } from "@/types/user";
import type { TenantItem } from "@/types/tenantService";
import useAuthStore from "@/store/useAuthStore";
import { Box, Button } from "@mui/material";
import type { AxiosError } from "axios";
import { toast } from "react-toastify";
import { Eye, EyeOff } from "lucide-react";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface AddUserProps {
  onClose: () => void;
  editUserData: User | null;
  companyinfo?: boolean | null;
}



const Add_User = ({ onClose, editUserData, companyinfo }: AddUserProps) => {
  const { isDark } = useDashboardTheme();
  const [tenantData, setTenantData] = useState<TenantItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);

  const tenantId = localStorage.getItem("tenantId");
  const { data: allocationsData } = useAllocations();
  const { data: creditUsageData } = useCreditUsage(
    { tenantId: tenantId ?? undefined },
    !!tenantId
  );

  const availablePoolCredits = useMemo(() => {
    // 1. Check creditUsageData balances for organisation pool balance (where userId is null or undefined)
    if (creditUsageData) {
      const usageRes = (creditUsageData as any)?.data || creditUsageData;
      const balances = usageRes?.balances || [];
      if (Array.isArray(balances)) {
        const orgBalanceObj = balances.find((b: any) => b.userId === null || !b.userId);
        if (orgBalanceObj && typeof orgBalanceObj.balance === "number") {
          return orgBalanceObj.balance;
        }
      }
    }

    // 2. Check allocationsData
    if (allocationsData) {
      const allocRes = (allocationsData as any)?.data || allocationsData;
      const allocs = allocRes?.allocations || (Array.isArray(allocRes) ? allocRes : []);
      if (Array.isArray(allocs)) {
        const orgAlloc = allocs.find((a: any) => a.tenant || (!a.user && !a.userId));
        if (orgAlloc) {
          return orgAlloc.remaining ?? orgAlloc.credits ?? 0;
        }
      }
    }

    // 3. Check tenantData
    if (tenantData) {
      const t = tenantData as any;
      if (typeof t.availableCredits === "number") return t.availableCredits;
      if (typeof t.credits === "number") return t.credits;
    }

    return 0;
  }, [creditUsageData, allocationsData, tenantData]);





  const [formData, setFormData] = useState<{
    fullName: string;
    email: string;
    companyName: string;
    password: string;
    role: string;
    initialCredits: string | number;
  }>({
    fullName: "",
    email: "",
    companyName: "",
    password: "",
    role: "USER",
    initialCredits: "",
  });

  const [errors, setErrors] = useState<Record<string, boolean>>({
    fullName: false,
    email: false,
    password: false,
  });

  const { mutate: createUser, isPending } = useCreateUser();
  const { mutate: updateUser } = useUpdateUser();
  const { role } = useAuthStore();

  useEffect(() => {
    if (editUserData) {
      setFormData({
        fullName: editUserData?.name || "",
        email: editUserData?.email || "",
        companyName: editUserData?.companyName || "",
        password: "",
        role: editUserData.role || "USER",
        initialCredits: "",
      });
    } else {
      setFormData({
        fullName: "",
        email: "",
        companyName: "",
        password: "",
        role: role || "USER",
        initialCredits: "",
      });
    }
  }, [editUserData, role]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "email") {
      setFormError(null);
    }

    const validatableFields = ["fullName", "email", "password", "companyName"] as const;

    if (validatableFields.includes(name as any)) {
      setErrors((prev) => ({
        ...prev,
        [name]: !isValid(name as any, value),
      }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const validatableFields = ["fullName", "email", "companyName", "password"] as const;
    if (validatableFields.includes(name as any)) {
      setErrors((prev) => ({
        ...prev,
        [name]: !isValid(name as any, value),
      }));
    }
  };

  const executeCreateUser = () => {
    const validationResult = {
      fullName: !isValid("fullName", formData.fullName),
      email: !isValid("email", formData.email),
      password: !editUserData && !isValid("password", formData.password),
    };

    setErrors(validationResult);

    const hasAnyErrors = Object.values(validationResult).some(Boolean);
    if (hasAnyErrors) return;

    const payload = {
      email: formData.email,
      name: formData.fullName,
      tenantType: "INDIVIDUAL" as const,
      companyName: formData.companyName,
      role: formData.role,
    };
    const payloadCreat = {
      email: formData.email,
      name: formData.fullName,
      tenantId: tenantId ?? "",
      password: formData.password,
      role: formData.role,
      initialCredits: formData.initialCredits ? Number(formData.initialCredits) : 0,
    };

    if (editUserData) {
      updateUser(
        { id: editUserData.id, ...payload },
        {
          onSuccess: () => {
            toast.success("User Updated Successfully");
            queryClient.invalidateQueries({ queryKey: ["users"] });
            onClose();
          },
          onError: (err) => {
            console.error("Update Error:", err);
            setFormError("Failed to update user. Please try again.");
            toast.error("Failed to update user. Please try again.");
          },
        },
      );
    } else {
      createUser(payloadCreat, {
        onSuccess: () => {
          toast.success("User Created Successfully");
          queryClient.invalidateQueries({ queryKey: ["users"] });
          queryClient.invalidateQueries({ queryKey: ["allocations"] });
          onClose();
        },
        onError: (error) => {
          const err = error as AxiosError<any>;
          console.error("Create Error:", err);

          const errorData = err?.response?.data;
          const msg = errorData?.message || errorData?.data?.message || "";

          let message = "An unexpected error occurred. Please try again.";

          if (
            err?.response?.status === 409 ||
            msg.includes("Email already exists")
          ) {
            message = "Email already exists. Please choose a different email.";
            setErrors((prev) => ({
              ...prev,
              email: true,
            }));
          } else if (msg) {
            message = msg;
          }

          setFormError(message);
          toast.error(message);
        },
      });
    }
  };

  const handleSave = () => {
    const validationResult = {
      fullName: !isValid("fullName", formData.fullName),
      email: !isValid("email", formData.email),
      password: !editUserData && !isValid("password", formData.password),
    };

    setErrors(validationResult);

    const hasAnyErrors = Object.values(validationResult).some(Boolean);
    if (hasAnyErrors) return;

    if (editUserData) {
      executeCreateUser();
      return;
    }

    const requested = formData.initialCredits ? Number(formData.initialCredits) : 0;

    if (requested > availablePoolCredits) {
      const msg = `Insufficient organisation pool credits. Available: ${availablePoolCredits}, Requested: ${requested}. User creation cancelled.`;
      setFormError(msg);
      toast.error(msg);
      return;
    }

    executeCreateUser();
  };

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        if (!tenantId) throw new Error("No tenant ID found");
        const tenant = await getTenantById(tenantId, {});
        setTenantData(
          tenant
            ? {
              ...tenant,
              domain: tenant.domain === null ? undefined : tenant.domain,
            }
            : null,
        );
      } catch {
        setError("Error fetching tenant");
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, [tenantId]);

  if (loading) return <p className={isDark ? "text-white" : "text-gray-900"}>Loading...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className={"space-y-6 " + (isDark ? "bg-black text-white" : "light-theme-dashboard")}>
      <div className="bg-[#0F0E15] p-6 rounded-2xl">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div>
            <p className="text-gray-400">Company Name</p>
            <p className="font-medium">{tenantData?.name}</p>
          </div>
          <div>
            <p className="text-gray-400">Company Email</p>
            <p className="font-medium">{tenantData?.contactEmail}</p>
          </div>
          <div>
            <p className="text-gray-400">Website Name</p>
            <p className="font-medium">{tenantData?.domain}</p>
          </div>
          <div>
            <p className="text-gray-400">Industry Type</p>
            <p className="font-medium">{tenantData?.type}</p>
          </div>
        </div>
      </div>

      {!companyinfo && (
        <div className="bg-[#0F0E15] p-6 rounded-2xl">
          <h2 className="text-lg font-semibold mb-6">Primary Contact Person</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <CustomInput
              label="Full Name"
              name="fullName"
              placeholder="Enter Full Name"
              value={formData.fullName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.fullName ? "Valid full name required" : ""}
              required
              disable={role === "MANAGER"}
            />
            <CustomInput
              label="Email Address"
              name="email"
              placeholder="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              disable={!!editUserData?.id}
              error={
                errors.email
                  ? formError && formError.includes("Email already exists")
                    ? formError
                    : "Valid email required"
                  : ""
              }
              required
            />

            {!editUserData?.id && (
              <div className="relative">
                <CustomInput
                  label="Password"
                  name="password"
                  placeholder="Enter Password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    errors.password
                      ? "Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character."
                      : ""
                  }
                  required
                  disable={role === "MANAGER"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-gray-400 hover:text-gray-200"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            )}

            <div>
              <label className="block text-sm text-white font-bold mb-1">
                Role<span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={role === "MANAGER"}
                className="bg-[#2C293A] border border-gray-500 text-white rounded-3xl px-4 py-2 w-full focus:outline-none"
              >
                <option value="ADMIN">ADMIN</option>
                <option value="MANAGER">MANAGER</option>
                <option value="USER">USER</option>
              </select>
            </div>

            {!editUserData?.id && (
              <div>
                <CustomInput
                  label="Credit Allocation (Optional)"
                  name="initialCredits"
                  placeholder="e.g. 30"
                  type="number"
                  value={formData.initialCredits === "" ? "" : String(formData.initialCredits)}
                  onChange={handleChange}
                  disable={role === "MANAGER"}
                />
                <p className="text-xs text-gray-400 mt-1 pl-1 font-medium">
                  Available Organisation Pool: <span className="font-bold text-[#C1FF72]">{availablePoolCredits} Credits</span>
                </p>
              </div>
            )}
          </div>

          {role !== "MANAGER" && (
            <Box display="flex" justifyContent="flex-end" mt={1}>
              <Button
                onClick={handleSave}
                sx={{
                  width: "fit-content",
                  backgroundColor: "#C1FF72",
                  color: "#000",
                  fontWeight: "bold",
                  borderRadius: 999,
                  px: 3,
                  py: 1,
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#D7FF9B" },
                }}
              >
                {editUserData ? (
                  "Update User"
                ) : isPending ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "CREATE"
                )}
              </Button>
            </Box>
          )}
        </div>
      )}
    </div>
  );
};

export default Add_User;
